import React from "react";
import { Shield, UserCheck, Ban, UserX, Lock, Clock, KeyRound } from "lucide-react";
import FilterChip from "@/components/ui/FilterChip";
import { ROLE_OPTIONS } from "./roleLabels";

/**
 * Filter chips over the users list.
 *
 * The state is kept as a single object `filters = { role, status, flags }`
 * where `flags` is a Set of independent booleans (secAdmin, recentlyActive
 * and mfa). This mirrors the enterprise-console pattern where any chip can
 * be toggled independently and every chip narrows the visible list.
 */
export const DEFAULT_FILTERS = {
    role: "any",     // "any" | "installer" | "customer" | "operations"
    status: "any",   // "any" | "active" | "suspended" | "disabled" | "locked"
    flags: new Set(),// subset of {"secAdmin","recentlyActive","mfa"}
};

const STATUS_CHIPS = [
    { key: "active",    label: "Active",    tone: "green" },
    { key: "disabled",  label: "Disabled",  tone: "red" },
    { key: "suspended", label: "Suspended", tone: "amber" },
    { key: "locked",    label: "Locked",    tone: "red", icon: Lock },
];

// DEV-003G.4 — Operations is now a first-class role in the console.
// Order: Customer → Engineer → Operations matches the create/edit form.
const ROLE_CHIPS = ROLE_OPTIONS.map((o) => ({ key: o.value, label: o.label }));

export default function UserFilters({ filters, setFilters, counts = {} }) {
    const toggleRole   = (r) => setFilters((f) => ({ ...f, role:   f.role   === r ? "any" : r }));
    const toggleStatus = (s) => setFilters((f) => ({ ...f, status: f.status === s ? "any" : s }));
    const toggleFlag   = (name) => setFilters((f) => {
        const next = new Set(f.flags);
        next.has(name) ? next.delete(name) : next.add(name);
        return { ...f, flags: next };
    });

    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid="admin-sec-user-filters"
        >
            <div className="flex flex-wrap items-center gap-1.5">
                {ROLE_CHIPS.map((c) => (
                    <FilterChip
                        key={c.key}
                        active={filters.role === c.key}
                        onClick={() => toggleRole(c.key)}
                        count={counts.role?.[c.key]}
                        testid={`admin-sec-filter-role-${c.key}`}
                    >
                        {c.label}
                    </FilterChip>
                ))}
            </div>
            <span className="h-4 w-px bg-[#E5E7EB] mx-1" />
            <div className="flex flex-wrap items-center gap-1.5">
                {STATUS_CHIPS.map((c) => (
                    <FilterChip
                        key={c.key}
                        active={filters.status === c.key}
                        onClick={() => toggleStatus(c.key)}
                        icon={c.icon}
                        tone={c.tone}
                        count={counts.status?.[c.key]}
                        testid={`admin-sec-filter-status-${c.key}`}
                    >
                        {c.label}
                    </FilterChip>
                ))}
            </div>
            <span className="h-4 w-px bg-[#E5E7EB] mx-1" />
            <div className="flex flex-wrap items-center gap-1.5">
                <FilterChip
                    active={filters.flags.has("secAdmin")}
                    onClick={() => toggleFlag("secAdmin")}
                    icon={Shield}
                    count={counts.secAdmin}
                    testid="admin-sec-filter-sec-admin"
                >
                    Security admin
                </FilterChip>
                <FilterChip
                    active={filters.flags.has("recentlyActive")}
                    onClick={() => toggleFlag("recentlyActive")}
                    icon={Clock}
                    count={counts.recentlyActive}
                    testid="admin-sec-filter-recent"
                >
                    Recently active
                </FilterChip>
                <FilterChip
                    active={filters.flags.has("mfa")}
                    onClick={() => toggleFlag("mfa")}
                    icon={KeyRound}
                    count={counts.mfa}
                    disabled
                    testid="admin-sec-filter-mfa"
                >
                    MFA enabled
                </FilterChip>
            </div>
        </div>
    );
}

/**
 * Pure client-side filter — the API call sends only the free-text
 * search string; role/status/flag filtering is done here so a single
 * fetch supports composing arbitrary chips.
 */
export function applyUserFilters(users, filters) {
    const recentCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return users.filter((u) => {
        if (filters.role !== "any" && u.role !== filters.role) return false;
        if (filters.status !== "any") {
            if (filters.status === "locked") {
                const lockedNow = u.locked_until && new Date(u.locked_until).getTime() > Date.now();
                if (!lockedNow) return false;
            } else if (u.account_status !== filters.status) {
                return false;
            }
        }
        if (filters.flags.has("secAdmin") && !u.is_security_admin) return false;
        if (filters.flags.has("recentlyActive")) {
            const t = u.last_successful_login_at ? new Date(u.last_successful_login_at).getTime() : 0;
            if (!t || t < recentCutoff) return false;
        }
        if (filters.flags.has("mfa") && !u.mfa_enabled) return false;
        return true;
    });
}

export function computeUserFilterCounts(users) {
    const counts = {
        role:   { installer: 0, customer: 0, operations: 0 },
        status: { active: 0, disabled: 0, suspended: 0, locked: 0 },
        secAdmin: 0,
        recentlyActive: 0,
        mfa: 0,
    };
    const recentCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    for (const u of users) {
        if (counts.role[u.role] !== undefined) counts.role[u.role] += 1;
        const lockedNow = u.locked_until && new Date(u.locked_until).getTime() > Date.now();
        if (lockedNow) counts.status.locked += 1;
        if (counts.status[u.account_status] !== undefined) counts.status[u.account_status] += 1;
        if (u.is_security_admin) counts.secAdmin += 1;
        const t = u.last_successful_login_at ? new Date(u.last_successful_login_at).getTime() : 0;
        if (t && t >= recentCutoff) counts.recentlyActive += 1;
        if (u.mfa_enabled) counts.mfa += 1;
    }
    return counts;
}
